import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AiSparkle } from "@/components/common/AiSparkle";
import { LOGO_SRC } from "@/components/common/ui";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";

const inputCls = "h-11 text-[15px]";
const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5"><Label htmlFor={id} className="text-[13px] font-normal text-neutral-600">{label}</Label><Input id={id} className={inputCls} {...props} /></div>
);
const Submit = ({ busy, children, testId }) => (
  <button type="submit" disabled={busy} data-testid={testId}
    className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white transition-colors hover:bg-neutral-800 disabled:opacity-60">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />} {children}
  </button>
);

/** Global sign-in / create-account dialog, opened by requireAccount() (saving homes, AI after the free try). */
export const AuthDialog = () => {
  const { prompt, closePrompt, login, register } = useAuth();
  const [tab, setTab] = useState("register");
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (prompt) {
      setTab(prompt.tab || "register");
      setError("");
    }
  }, [prompt]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const run = async (e, fn) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await fn();
      setF({ name: "", email: "", password: "", phone: "" });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(prompt)} onOpenChange={(o) => !o && closePrompt()}>
      <DialogContent className="z-[70] max-h-[92vh] overflow-y-auto sm:max-w-md" data-testid="auth-dialog">
        <img src={LOGO_SRC} alt="757 Realty" className="h-9 w-auto self-start" />
        <DialogTitle className="mt-2 text-2xl font-normal">{tab === "register" ? "Create your free account" : "Welcome back"}</DialogTitle>
        <DialogDescription className="flex items-start gap-2 text-[14px] text-neutral-600" data-testid="auth-dialog-reason">
          <AiSparkle className="mt-0.5 h-4 w-4 shrink-0" />
          {prompt?.reason || "Save homes, use AI search and the AI home assistant, and pick up where you left off on any device."}
        </DialogDescription>
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-2 rounded-full">
            <TabsTrigger value="register" className="rounded-full" data-testid="auth-tab-register">Create account</TabsTrigger>
            <TabsTrigger value="login" className="rounded-full" data-testid="auth-tab-login">Sign in</TabsTrigger>
          </TabsList>
          <TabsContent value="register">
            <form className="mt-3 space-y-3" onSubmit={(e) => run(e, () => register({ name: f.name, email: f.email, password: f.password, phone: f.phone }))} data-testid="register-form">
              <Field id="reg-name" label="Name *" value={f.name} onChange={set("name")} autoComplete="name" data-testid="register-name-input" />
              <Field id="reg-email" label="Email *" type="email" value={f.email} onChange={set("email")} autoComplete="email" data-testid="register-email-input" />
              <Field id="reg-password" label="Password * (8+ characters)" type="password" value={f.password} onChange={set("password")} autoComplete="new-password" data-testid="register-password-input" />
              <Field id="reg-phone" label="Phone (optional)" type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" data-testid="register-phone-input" />
              {error && <p className="rounded-md bg-red-50 p-3 text-[14px] text-red-700" data-testid="auth-error">{error}</p>}
              <Submit busy={busy} testId="register-submit-button">Create free account</Submit>
            </form>
          </TabsContent>
          <TabsContent value="login">
            <form className="mt-3 space-y-3" onSubmit={(e) => run(e, () => login(f.email, f.password))} data-testid="login-form">
              <Field id="login-email" label="Email" type="email" value={f.email} onChange={set("email")} autoComplete="username" data-testid="login-email-input" />
              <Field id="login-password" label="Password" type="password" value={f.password} onChange={set("password")} autoComplete="current-password" data-testid="login-password-input" />
              {error && <p className="rounded-md bg-red-50 p-3 text-[14px] text-red-700" data-testid="auth-error">{error}</p>}
              <Submit busy={busy} testId="login-submit-button">Sign in</Submit>
              <p className="text-center text-[13px] text-neutral-500">Forgot your password? Call {"(757) 517-8050"} and we'll reset it.</p>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
